import { useEffect, useMemo, useState } from 'react';
import { cn } from '@workspace/ui/lib/utils';

export interface PromoCountdownParts {
    hours: number;
    minutes: number;
    seconds: number;
    totalSeconds: number;
}

export function getPromoCountdownParts(
    endTime: string | null | undefined,
    now = Date.now()
): PromoCountdownParts | null {
    if (!endTime) return null;

    const endTimeMs = Date.parse(endTime);
    if (!Number.isFinite(endTimeMs)) return null;

    const totalSeconds = Math.ceil((endTimeMs - now) / 1000);
    if (totalSeconds <= 0) return null;

    return {
        hours: Math.floor(totalSeconds / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
        totalSeconds,
    };
}

export function formatPromoCountdown(parts: PromoCountdownParts): string {
    return [parts.hours, parts.minutes, parts.seconds]
        .map((value) => String(value).padStart(2, '0'))
        .join(':');
}

export function usePromoCountdown(endTime: string | null | undefined) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        setNow(Date.now());
        if (!endTime || !Number.isFinite(Date.parse(endTime))) return;

        const syncNow = () => setNow(Date.now());
        const timer = window.setInterval(syncNow, 1000);
        document.addEventListener('visibilitychange', syncNow);

        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', syncNow);
        };
    }, [endTime]);

    return useMemo(() => getPromoCountdownParts(endTime, now), [endTime, now]);
}

export function PromoCountdown({
    endTime,
    className,
    testId = 'promo-countdown',
}: {
    endTime: string | null | undefined;
    className?: string;
    testId?: string | null;
}) {
    const parts = usePromoCountdown(endTime);
    if (!parts) return null;

    return (
        <time
            dateTime={endTime ?? undefined}
            className={cn('whitespace-nowrap tabular-nums', className)}
            data-testid={testId}
        >
            {formatPromoCountdown(parts)}
        </time>
    );
}
